import React, { lazy, Suspense, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Route, Switch, useHistory, useParams } from "react-router-dom";

import DateFnsUtils from "@date-io/date-fns";
import PropTypes from "prop-types";

import CssBaseline from "@material-ui/core/CssBaseline";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

import Loader from "../../Components/Loader/";
import PrivateRoute from "../../Components/PrivateRoute";

import actions from "../../Stores/Auth/actions";
import useItemValidator from "../../utils/useItemValidator";
import Alerts from "../Alerts/";
import Login from "../Login";
import PackageTabs from "../PackageTabs";
import ReviewBadge from "../ReviewBadge";
import ReviewCard from "../ReviewCard";
import ReviewExport from "../ReviewExport";
import ReviewPoster from "../ReviewPoster";
import ReviewScore from "../ReviewScore";
import ViewAction from "../Survey/SurveyAction/ViewAction";
import Website from "../Website";
import Pages from "../Website/Pages/";
import PagesAdd from "../Website/Pages/PageAdd";
import WebsiteAdd from "../Website/WebsiteAdd";

import WebsiteEnquiresAdd from "../Location/WebsiteEnquires/WebsiteEnquiresAdd";

import Intercom from "@intercom/messenger-js-sdk";
import { generateHmac } from "../../utils/crypto";
import HubAdd from "../CareHomeGroups/Hub/HubAdd";
import WebsiteTabs from "../Location/WebsiteEnquires/WebsiteTabs";

const { check } = actions;

const DashBoard = lazy(() => import("./../DashBoard/"));
const UserDashboard = lazy(() => import("./../UserDashboard"));

const CompanyProfile = lazy(() =>
  import("../CareHomeGroups/CareHomeGroupsAdd")
);

const CompanyTabs = lazy(() =>
  import("../CareHomeGroups/CareHomeGroupsAdd/CompanyTabs")
);

const UserManagement = lazy(() => import("../UserManagement"));
const Admins = lazy(() => import("../CareHomeGroups/Admins"));
const Staffs = lazy(() => import("../CareHomeGroups/Staffs"));

const Reviews = lazy(() => import("../Reviews"));
const ReviewsEdit = lazy(() => import("../Reviews/ReviewsEdit"));
const ReviewsImage = lazy(() => import("../Reviews/ReviewsImage"));

const UploadCard = lazy(() => import("../UploadCard"));
const UploadCardAdd = lazy(() => import("../UploadCard/UploadCardAdd"));

const LocationEdit = lazy(() => import("../Location/LocationEdit"));

const Enquiries = lazy(() => import("../Enquiries/index"));
const EnquiriesAdd = lazy(() => import("../Enquiries/EnquiriesAdd/"));
const EnquiriesEdit = lazy(() => import("../Enquiries/EnquiriesEdit/"));

const News = lazy(() => import("../News/index"));
const NewsAdd = lazy(() => import("../News/NewsAdd/"));

const Network = lazy(() => import("../Network/index"));
const NetworkAdd = lazy(() => import("../Network/NetworkAdd/"));

const Blogs = lazy(() => import("../Location/Blogs/"));
const BlogAdd = lazy(() => import("../Location/Blogs/BlogsAdd/"));

const WebsiteEnquires = lazy(() => import("../Location/WebsiteEnquires/"));

const Faqs = lazy(() => import("../Location/FAQs"));
const FaqAdd = lazy(() => import("../Location/FAQs/FAQAdd/"));

const EmailSettings = lazy(() => import("../Location/EmailSettings/"));

const Invites = lazy(() => import("../Location/Invitees"));
const History = lazy(() => import("../Location/History"));

const NotFoundPage = lazy(() => import("../NotFound/"));
const UserProfile = lazy(() => import("./../UserProfile/"));
const ChangePassword = lazy(() => import("./../ChangePassword/"));
const ResetPassword = lazy(() => import("../ResetPassword/"));
const SetPassword = lazy(() => import("../SetPassword/"));
const ForgotPassword = lazy(() => import("../ForgotPassword/"));
const Register = lazy(() => import("../Register/"));
const NoPermission = lazy(() => import("../NoPermission"));

const ReviewLink = lazy(() => import("../Promotion/Share"));

const Payment = lazy(() => import("../Promotion/Payment"));
const Confirmation = lazy(() => import("../Promotion/Payment/Confirmation"));
const LocationSelector = lazy(() => import("../Promotion/LocationSelector"));

const Task = lazy(() => import("../Task"));

const SurveyAdd = lazy(() => import("../Survey/SurveyAdd"));
const SurveyTabs = lazy(() => import("../Survey/SurveyTabs"));
const ViewResponse = lazy(() =>
  import("../Survey/SurveyResponses/ViewResponse")
);
const SurveyMain = lazy(() => import("../Survey/SurveyMain"));
const SurveyListing = lazy(() => import("../Survey/Listing"));

const ReportTabs = lazy(() => import("../Enquiries/ReportTabs/"));

const Subscriptions = lazy(() => import("../Subscriptions/"));
const SubLocationSelector = lazy(() =>
  import("../Subscriptions/SubLocationSelector")
);
const SubPayment = lazy(() => import("../Subscriptions/SubPayment"));
const SubConfirmation = lazy(() =>
  import("../Subscriptions/SubPayment/Confirmation")
);
const Dashboard = lazy(() => import("../Subscriptions/Dashboard"));
const AddPayment = lazy(() => import("../Subscriptions/AddPayment"));

// Roster
const Roster = lazy(() => import("../Roster/"));
const RosterCalendar = lazy(() => import("../Roster/RosterCalendar/"));
const RosterAllocation = lazy(() => import("../Roster/RosterAllocation/"));
const Employee = lazy(() => import("../Roster/Employee/"));
const EmployeeAdd = lazy(() => import("../Roster/Employee/EmployeeAdd/"));
const EmployeeEdit = lazy(() => import("../Roster/Employee/EmployeeEdit/"));
const EmployeeCSV = lazy(() => import("../Roster/Employee/EmployeeCSV"));
const RosterRole = lazy(() => import("../Roster/Role/"));
const RosterRoleAdd = lazy(() => import("../Roster/Role/RoleAdd/"));
const LeaveTabs = lazy(() => import("../Roster/Leave/LeaveTabs"));
const LeaveHistory = lazy(() => import("../Roster/Leave/LeaveHistory/"));
const ReportingList = lazy(() => import("../Roster/Leave/ReportingList"));
const Timesheet = lazy(() => import("../Roster/Timesheet/"));
const TimesheetAllList = lazy(() => import("../Roster/Timesheet/AllList/"));
const Payroll = lazy(() => import("../Roster/Timesheet/Payroll/"));
const AllocationReport = lazy(() =>
  import("../Roster/Timesheet/AllocationReport/")
);
const Budget = lazy(() => import("../Roster/Budget/"));
const BudgetAdd = lazy(() => import("../Roster/Budget/BudgetAdd/"));
const RosterSettings = lazy(() => import("../Roster/RosterSettings"));
const Discrepancy = lazy(() => import("../Roster/Discrepancy/"));
const Availability = lazy(() => import("../Roster/Availability/"));

const Hub = lazy(() => import("../CareHomeGroups/Hub/"));

const Users = lazy(() => import("../Users/"));
const UserEdit = lazy(() => import("../Roster/Employee/UserEdit/"));

const Terminal = lazy(() => import("../Roster/Terminal/"));
const TerminalAdd = lazy(() => import("../Roster/Terminal/TerminalAdd/"));

const Team = lazy(() => import("../Roster/Team/"));
const TeamAdd = lazy(() => import("../Roster/Team/TeamAdd/"));

const Notification = lazy(() => import("../Roster/Notification/"));

const Feed = lazy(() => import("../Roster/Feed/"));
const AddFeed = lazy(() => import("../Roster/Feed/AddFeed/"));
const FeedComment = lazy(() => import("../Roster/Feed/FeedComment/"));
const FeedOverview = lazy(() => import("../Roster/Feed/FeedOverview/"));

function RootApp({ isAuthenticating, isAuthenticated, checkAuth, user }) {
  const history = useHistory();
  const [authFallback, setAuthFallback] = useState(true);
  const [request, setRequest] = useState(false);
  const itemValidator = useItemValidator();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const isReset = history.location.pathname.startsWith("/reset-password/");
    const isSet = history.location.pathname.startsWith("/set-password/");

    if (token) {
      checkAuth(token, history);
      setRequest(true);
      if (user?.id) {
        Intercom({
          app_id: process.env.REACT_APP_INTERCOM_ID,
          email: user?.email,
          user_id: user?.id,
          user_hash: generateHmac(user?.id, process.env.REACT_APP_HMAC_SECRET),
          name: `${user?.first_name} ${user?.last_name}`,
          created_at: Date.now(),
        });
      }
    } else {
      setAuthFallback(false);
      if (!isReset && !isSet) {
        history.replace({
          pathname: "/",
          state: {
            referrer: history.location,
            params: history.location.pathname,
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isAuthenticating) {
      setAuthFallback(false);
      setTimeout(() => {
        setRequest(false);
      }, 1000);
    }
  }, [isAuthenticated, isAuthenticating]);

  return (
    <>
      <Alerts />
      <Suspense fallback={<Loader open />}>
        {isAuthenticating || authFallback || request ? (
          <>
            <Loader open />
          </>
        ) : (
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <CssBaseline />
            <Switch>
              {/* AUTH STARTS */}
              <Route path="/" exact component={Login} />
              <Route path="/login" component={Login} />
              <Route
                path="/reset-password/:email/:token"
                component={ResetPassword}
              />
              <Route path="/set-password/:id" component={SetPassword} />
              <Route path="/admin/register" component={Register} />
              <Route path="/forgot/password" component={ForgotPassword} />
              {/* AUTH ENDS */}

              <PrivateRoute path="/d">
                <DashBoard>
                  <Suspense fallback={<Loader open />}>
                    <Switch>
                      {/* New Routes */}
                      <Route exact path="/d/home" component={UserDashboard} />
                      {/* New Routes */}

                      {/* Roster Start */}
                      <Route
                        exact
                        path="/d/rota/week"
                        render={() =>
                          itemValidator(<RosterCalendar />, `ROSTER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/month"
                        component={RosterAllocation}
                      />
                      <Route
                        exact
                        path="/d/r/hr/roles"
                        render={() =>
                          itemValidator(
                            <RosterRole />,
                            `ROSTER-ROLES`,
                            `DISPLAY`
                          )
                        }
                      />

                      <Route
                        exact
                        path="/d/hr/employee"
                        render={() =>
                          itemValidator(
                            <Employee isUser={false} />,
                            `EMPLOYEES`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/upload"
                        render={() =>
                          itemValidator(
                            <EmployeeCSV isUser={false} />,
                            `EMPLOYEES`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/add"
                        render={() =>
                          itemValidator(
                            <EmployeeAdd isUser={false} />,
                            `EMPLOYEES`,
                            `WRITE`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/personal"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/roles"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/locations"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/schedule"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/documents"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/payroll"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/roles"
                        render={() =>
                          itemValidator(
                            <RosterRole />,
                            `ROSTER-ROLES`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/roles/add"
                        render={() =>
                          itemValidator(
                            <RosterRoleAdd />,
                            `ROSTER-ROLES`,
                            `WRITE`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/roles/edit/:id"
                        render={() =>
                          itemValidator(
                            <RosterRoleAdd />,
                            `ROSTER-ROLES`,
                            `WRITE`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/calendar"
                        render={() =>
                          itemValidator(<LeaveTabs />, `LEAVE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/requests"
                        render={() =>
                          itemValidator(<LeaveTabs />, `LEAVE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/manager"
                        render={() =>
                          itemValidator(<LeaveTabs />, `LEAVE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/history"
                        render={() =>
                          itemValidator(<LeaveHistory />, `LEAVE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/list"
                        render={() =>
                          itemValidator(<Timesheet />, `TIMESHEET`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/clock"
                        render={() =>
                          itemValidator(
                            <TimesheetAllList />,
                            `TIMESHEET-REPORT`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/analysis"
                        render={() =>
                          itemValidator(
                            <AllocationReport />,
                            `TIMESHEET-REPORT`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/payroll/:from_date/:to_date"
                        render={() =>
                          itemValidator(<Payroll />, `TIMESHEET`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/budget"
                        render={() =>
                          itemValidator(<Budget />, `BUDGET`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/budget/add"
                        render={() =>
                          itemValidator(<BudgetAdd />, `BUDGET`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/budget/edit/:id"
                        render={() =>
                          itemValidator(<BudgetAdd />, `BUDGET`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/company/settings"
                        render={() =>
                          itemValidator(
                            <RosterSettings />,
                            `ROSTER-SETTINGS`,
                            `WRITE`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/extra-time"
                        render={() =>
                          itemValidator(
                            <Discrepancy />,
                            `DISCREPANCY`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/availability"
                        render={() =>
                          itemValidator(<Availability />, `ROSTER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/terminal"
                        render={() =>
                          itemValidator(<Terminal />, `ROSTER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/terminal/add"
                        render={() =>
                          itemValidator(<TerminalAdd />, `ROSTER`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/terminal/edit/:id"
                        render={() =>
                          itemValidator(<TerminalAdd />, `ROSTER`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/teams"
                        render={() =>
                          itemValidator(<Team />, `ROSTER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/teams/add"
                        render={() =>
                          itemValidator(<TeamAdd />, `ROSTER`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/teams/edit/:id"
                        render={() =>
                          itemValidator(<TeamAdd />, `ROSTER`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/all"
                        render={() => itemValidator(<Feed />, `FEED`, `WRITE`)}
                      />
                      <Route
                        exact
                        path="/d/feed/add"
                        render={() =>
                          itemValidator(<AddFeed />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/report"
                        render={() =>
                          itemValidator(<FeedOverview />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/edit/:id"
                        render={() =>
                          itemValidator(<AddFeed />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/comments/:id"
                        render={() =>
                          itemValidator(<FeedComment />, `FEED`, `WRITE`)
                        }
                      />

                      {/* Roster End */}

                      <Route
                        exact
                        path="/d/no-permission"
                        component={NoPermission}
                      />
                      <Route
                        exact
                        path="/d/myprofile"
                        component={UserProfile}
                      />
                      <Route
                        exact
                        path="/d/myprofile/notification-settings"
                        component={Notification}
                      />
                      <Route
                        exact
                        path="/d/change-password"
                        component={ChangePassword}
                      />
                      <Route
                        exact
                        path="/d/company/overview/:id"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <CompanyTabs />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/subscribe"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <CompanyTabs />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/billing"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <CompanyTabs />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/hub"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <CompanyTabs />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/hub/add"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <HubAdd />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/hub/edit/:id"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <HubAdd />
                        )}
                      />

                      {/* LOCATION STARTS */}
                      <Route
                        exact
                        path="/d/payment/location-selector"
                        render={() =>
                          itemValidator(
                            <LocationSelector />,
                            `LOCATION`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/:id/profile/:step"
                        component={() =>
                          itemValidator(
                            <LocationSelector />,
                            `LOCATION`,
                            `WRITE`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/base-data"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/care-offered"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/facilities"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/funding-fees"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/covid-info"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/description"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/photos"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/videos"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/teams"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/tour"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/team"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      {/* Location Ends */}
                      <Route
                        exact
                        path="/d/location/edit/:id/posts"
                        component={() =>
                          itemValidator(<Blogs />, `BLOGS`, `DISPLAY`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/posts/add"
                        render={() =>
                          itemValidator(<BlogAdd />, `BLOGS`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/posts/edit/:postId"
                        render={() =>
                          itemValidator(<BlogAdd />, `BLOGS`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/website-enquiries/listing"
                        render={() =>
                          itemValidator(
                            <WebsiteTabs isEditing={true} />,
                            `LOCATION`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/website-enquiries/acquisition"
                        render={() =>
                          itemValidator(
                            <WebsiteTabs isEditing={true} />,
                            `LOCATION`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/website-enquiries/add"
                        component={() =>
                          itemValidator(
                            <WebsiteEnquiresAdd />,
                            `LOCATION`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/website-enquiries/edit/:webEnqID"
                        render={() =>
                          itemValidator(
                            <WebsiteEnquiresAdd />,
                            `LOCATION`,
                            `WRITE`
                          )
                        }
                      />
                      {/* Enquiry Start */}
                      <Route
                        exact
                        path="/d/leads/:stage(all|prospecting|first contact|viewing|viewing outcome|assessment|assessment outcome|closed)/"
                        render={() =>
                          itemValidator(<Enquiries />, `ENQUIRIES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/add"
                        render={() =>
                          itemValidator(<EnquiriesAdd />, `ENQUIRIES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/edit/:id"
                        render={() =>
                          itemValidator(<EnquiriesEdit />, `ENQUIRIES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/networking"
                        render={() =>
                          itemValidator(<Network />, `NETWORKING`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/task"
                        render={() =>
                          itemValidator(<Task />, `ENQUIRIES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/networking/add"
                        render={() =>
                          itemValidator(<NetworkAdd />, `NETWORKING`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/networking/edit/:id"
                        render={() =>
                          itemValidator(
                            <NetworkAdd isEditing={true} />,
                            `NETWORKING`,
                            `WRITE`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/reports/dashboard"
                        render={() =>
                          itemValidator(
                            <ReportTabs isEditing={true} />,
                            `ENQUIRIES`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/reports/close"
                        render={() =>
                          itemValidator(
                            <ReportTabs isEditing={true} />,
                            `ENQUIRIES`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/reports/acquisition"
                        render={() =>
                          itemValidator(
                            <ReportTabs isEditing={true} />,
                            `ENQUIRIES`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/reports/geography"
                        render={() =>
                          itemValidator(
                            <ReportTabs isEditing={true} />,
                            `ENQUIRIES`,
                            `DISPLAY`
                          )
                        }
                      />
                      {/* Enquiry End */}
                      {/* Post Start */}
                      <Route
                        exact
                        path="/d/post/news"
                        render={() =>
                          itemValidator(<News />, `NEWS`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/post/news/add"
                        render={() =>
                          itemValidator(<NewsAdd />, `NEWS`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/post/news/edit/:id"
                        render={() =>
                          itemValidator(
                            <NewsAdd isEditing={true} />,
                            `NEWS`,
                            `WRITE`
                          )
                        }
                      />
                      {/* Post End */}
                      <Route
                        exact
                        path="/d/location/edit/:id/faqs"
                        render={() =>
                          itemValidator(<Faqs />, `FAQS`, `DISPLAY`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/faqs/add"
                        render={() =>
                          itemValidator(<FaqAdd />, `FAQS`, `WRITE`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/faqs/edit/:faqID"
                        render={() =>
                          itemValidator(<FaqAdd />, `FAQS`, `WRITE`, true)
                        }
                      />
                      <Route
                        path="/d/location/edit/:id/email-settings"
                        render={() =>
                          itemValidator(
                            <EmailSettings />,
                            `EMAIL-SETTINGS`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      {/* LOCATION ENDS */}

                      {/* SURVEY STARTS */}
                      <Route
                        exact
                        path="/d/engage/all"
                        render={() =>
                          itemValidator(<SurveyListing />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/add"
                        render={() =>
                          itemValidator(<SurveyAdd />, `SURVEY`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/edit/:id"
                        render={() =>
                          itemValidator(<SurveyTabs />, `SURVEY`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/edit/:id/qr"
                        render={() =>
                          itemValidator(<SurveyTabs />, `SURVEY`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/edit/:id/invite"
                        render={() =>
                          itemValidator(
                            <SurveyTabs />,
                            `SURVEY-INVITE`,
                            `WRITE`
                          )
                        }
                      />

                      <Route
                        exact
                        path="/d/engage/edit/:id/responses"
                        render={() =>
                          itemValidator(
                            <SurveyTabs />,
                            `SURVEY-RESPONSES`,
                            `WRITE`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/response/view/:id"
                        render={() =>
                          itemValidator(
                            <ViewResponse />,
                            `SURVEY-RESPONSES`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/dashboard"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/overview"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/headlines"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/action"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/finding"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/action/:id"
                        render={() =>
                          itemValidator(<ViewAction />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      {/* SURVEY ENDS */}

                      {/* USER MANAGEMENT STARTS */}
                      <Route
                        exact
                        path="/d/account-users"
                        component={() =>
                          itemValidator(<UserManagement />, `ADMIN`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/account-users/admin"
                        component={() =>
                          itemValidator(<Admins />, `ADMIN`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/account-users/staff"
                        component={() =>
                          itemValidator(<Staffs />, `STAFF`, `DISPLAY`)
                        }
                      />
                      {/* USER MANAGEMENT END */}
                      {/* REVIEW STARTS */}
                      <Route
                        exact
                        path="/d/review/listing"
                        render={() =>
                          itemValidator(<Reviews />, `REVIEWS`, `DISPLAY`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-card/"
                        render={() =>
                          itemValidator(
                            <ReviewCard />,
                            `REVIEW-CARD`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-poster/"
                        render={() =>
                          itemValidator(
                            <ReviewPoster />,
                            `REVIEW-POSTER`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-upload"
                        component={() =>
                          itemValidator(
                            <UploadCard />,
                            `REVIEWS`,
                            `DISPLAY`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-upload/add"
                        component={() =>
                          itemValidator(
                            <UploadCardAdd />,
                            `REVIEWS`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/edit/:id/reviews/edit/:reviewID"
                        component={() =>
                          itemValidator(
                            <ReviewsEdit />,
                            `REVIEWS`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/edit/:id/reviews/edit/:reviewID/generate-image"
                        component={() =>
                          itemValidator(
                            <ReviewsImage />,
                            `REVIEWS`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/share"
                        render={() =>
                          itemValidator(
                            <ReviewLink />,
                            `REVIEW-SHARE`,
                            `DISPLAY`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/packages"
                        render={() =>
                          itemValidator(
                            <PackageTabs />,
                            `PACKAGES`,
                            `DISPLAY`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/packages/order-history"
                        render={() =>
                          itemValidator(
                            <PackageTabs />,
                            `PACKAGES`,
                            `DISPLAY`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/export"
                        render={() =>
                          itemValidator(
                            <ReviewExport />,
                            `REVIEWS`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/invites"
                        render={() =>
                          itemValidator(
                            <Invites />,
                            `REVIEW-INVITE`,
                            `DISPLAY`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/orders"
                        render={() =>
                          itemValidator(<History />, `ORDERS`, `DISPLAY`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-score"
                        render={() =>
                          itemValidator(
                            <ReviewScore />,
                            `REVIEW-SCORE`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-badge"
                        render={() =>
                          itemValidator(
                            <ReviewBadge />,
                            `REVIEW-BADGE`,
                            `WRITE`,
                            true
                          )
                        }
                      />
                      {/* Review Ends */}
                      {/* Subscription Starts */}
                      <Route
                        exact
                        path="/d/subscriptions/bundles"
                        render={() => <Subscriptions />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/bundles/location-selector"
                        render={() => <SubLocationSelector />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/bundles/payment"
                        render={() => <SubPayment />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/bundles/payment/confirmation"
                        render={() => <SubConfirmation />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/dashboard"
                        render={() => <Dashboard />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/add-payment"
                        render={() => <AddPayment />}
                      />
                      {/* Subscription Ends */}

                      {/* WEBSITE */}
                      <Route
                        exact
                        path="/d/website"
                        component={() =>
                          itemValidator(<Website />, `WEBSITE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/website/add"
                        component={() =>
                          itemValidator(<WebsiteAdd />, `WEBSITE`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/website/edit/:id"
                        component={() =>
                          itemValidator(<WebsiteAdd />, `WEBSITE`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/website/:id/pages"
                        component={() =>
                          itemValidator(<Pages />, `WEBSITE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/website/:id/pages/:pageID"
                        component={() =>
                          itemValidator(<PagesAdd />, `WEBSITE`, `WRITE`)
                        }
                      />
                      {/* WEBSITE */}
                      {/* STRIPE PAYMENT PAGES */}
                      <Route
                        exact
                        path="/d/payment"
                        component={() =>
                          itemValidator(<Payment />, `PACKAGES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/payment-confirmation"
                        component={() =>
                          itemValidator(<Confirmation />, `PACKAGES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/users"
                        component={() =>
                          itemValidator(
                            <Employee isUser={true} />,
                            `EMPLOYEEUSER`,
                            `DISPLAY`
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/users/edit/:id"
                        component={() =>
                          itemValidator(<UserEdit />, `EMPLOYEEUSER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/users/add"
                        render={() =>
                          itemValidator(
                            <EmployeeAdd isUser={true} />,
                            `EMPLOYEEUSER`,
                            `WRITE`
                          )
                        }
                      />

                      {/* STRIPE PAYMENT PAGES */}
                      <Route component={NotFoundPage} />
                    </Switch>
                  </Suspense>
                </DashBoard>
              </PrivateRoute>
            </Switch>
          </MuiPickersUtilsProvider>
        )}
      </Suspense>
    </>
  );
}
RootApp.propTypes = {
  isAuthenticating: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  checkAuth: PropTypes.func.isRequired,
};

const mapStateToProps = ({ AuthReducer }) => ({
  isAuthenticating: AuthReducer.isAuthenticating,
  isAuthenticated: AuthReducer.isAuthenticated,
  permissions: AuthReducer.permissions,
  user: AuthReducer.user,
});
const mapDispatchToProps = (dispatch) => ({
  checkAuth: (...params) => dispatch(check(...params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RootApp);
